/**
 * Call E2EE Key Derivation Module
 *
 * Derives per-call symmetric encryption keys for live audio/video/screenshare
 * calls. Keys are injected into the LiveKit E2EE layer.
 *
 * Uses LiveKit's Insertable Streams E2EE with Adieuu-managed keys.
 * A future iteration will introduce a custom Insertable Streams pipeline
 * using Adieuu's own cipher suite (ChaCha20-Poly1305 / AES-256-GCM per
 * crypto profile).
 *
 * @module crypto/call
 */
import { deriveKey, KDF_INFO } from '../kdf';
import { randomBytes, toBytes } from '../utils';
import { hybridKeyExchange, hybridDecapsulate, computeRoutingTag, WRAP_AAD_DOMAIN, WRAP_VERSION_AAD, } from '../encrypt/hybrid';
import { encrypt, decrypt } from '../encrypt/symmetric';
/** HKDF info string for call E2EE key derivation. */
export const CALL_E2EE_INFO = 'adieuu-call-e2ee-v1';
/** Call E2EE key size (256 bits). */
export const CALL_KEY_SIZE = 32;
/** HKDF info for Space voice-channel media keys (alias of KDF_INFO). */
export const VOICE_CHANNEL_MEDIA_INFO = KDF_INFO.VOICE_CHANNEL_MEDIA;
function assertNoNewline(value, label) {
    if (value.length === 0) {
        throw new Error(`Call wrap AAD ${label} is required`);
    }
    if (value.includes('\n')) {
        throw new Error(`Call wrap AAD ${label} must not contain newlines`);
    }
}
/**
 * Associated data for v2 call-key wraps.
 *
 * Binds the recipient identity, the device routing tag, and the call id
 * so a wrap cannot be replayed onto a different call or re-addressed to
 * another identity/device. Encoding matches {@link buildStaticWrapAad}:
 * newline-joined header (hex/base64 fields only) with a trailing newline.
 */
export function buildCallWrapAad(identityId, routingTag, callId) {
    assertNoNewline(identityId, 'identityId');
    assertNoNewline(routingTag, 'routingTag');
    assertNoNewline(callId, 'callId');
    return toBytes([WRAP_AAD_DOMAIN, 'call', identityId, routingTag, callId].join('\n') + '\n');
}
/**
 * Generates a fresh random call E2EE key.
 *
 * Used when a call is initiated. The key is then distributed to
 * participants via {@link wrapCallKeyForRecipient}.
 *
 * @returns 32-byte random key
 */
export function generateCallKey() {
    return randomBytes(CALL_KEY_SIZE);
}
/**
 * Derives a call E2EE key from existing conversation key material.
 *
 * Binds the derived key to a specific call via the callId salt, preventing
 * key reuse across calls even within the same conversation.
 *
 * @param conversationKeyMaterial - Sender key material or conversation secret
 * @param callId - Unique call identifier (used as HKDF salt)
 * @param profile - Crypto profile
 * @returns 32-byte derived call E2EE key
 */
export function deriveCallE2EEKey(conversationKeyMaterial, callId, profile = 'default') {
    if (conversationKeyMaterial.length < 16) {
        throw new Error('Conversation key material must be at least 16 bytes');
    }
    return deriveKey({
        ikm: conversationKeyMaterial,
        salt: toBytes(callId),
        info: CALL_E2EE_INFO,
        length: CALL_KEY_SIZE,
    }, profile);
}
/**
 * Derives a deterministic LiveKit media E2EE key from the per-Space Argon2 key.
 *
 * Every member with the channel's Cipher can derive the same key — no
 * server-side key distribution. IKM is the cached Argon2 space key (not the
 * unbound Community Cipher master key). Bound to spaceId + channelId so keys
 * are not reusable across channels.
 */
export function deriveVoiceChannelMediaKey(cipher, spaceId, channelId, spaceKey) {
    if (!spaceId || !channelId) {
        throw new Error('spaceId and channelId are required');
    }
    if (spaceKey.length < 16) {
        throw new Error('Space key must be at least 16 bytes');
    }
    return deriveKey({
        ikm: spaceKey,
        salt: toBytes(`${spaceId}:${channelId}`),
        info: VOICE_CHANNEL_MEDIA_INFO,
        length: CALL_KEY_SIZE,
    }, cipher.profile);
}
/**
 * Wraps a call E2EE key for a single recipient using hybrid encryption.
 *
 * @param callKey - The call E2EE key to distribute
 * @param recipientKeys - Recipient's public keys (ECDH + KEM)
 * @param recipientIdentityId - Recipient's identity ID
 * @param callId - Call id bound into wrap AAD (client-chosen, becomes the server call id)
 * @returns Wrapped call key for the recipient
 */
export function wrapCallKeyForRecipient(callKey, recipientKeys, recipientIdentityId, callId) {
    if (callKey.length !== CALL_KEY_SIZE) {
        throw new Error(`Call key must be ${CALL_KEY_SIZE} bytes`);
    }
    if (!callId) {
        throw new Error('callId is required to wrap a call key');
    }
    const { sharedSecret, ephemeralPublicKey, kemCiphertext } = hybridKeyExchange(recipientKeys.ecdh, recipientKeys.kem, recipientKeys.profile);
    const routingTag = computeRoutingTag(recipientKeys.ecdh, recipientKeys.kem);
    const aad = buildCallWrapAad(recipientIdentityId, routingTag, callId);
    const { ciphertext: wrappedKey, nonce: wrappingNonce } = encrypt(sharedSecret, callKey, recipientKeys.profile, undefined, aad);
    return {
        recipientIdentityId,
        ephemeralPublicKey,
        kemCiphertext,
        wrappedKey,
        wrappingNonce,
        routingTag,
        wrapVersion: WRAP_VERSION_AAD,
    };
}
/**
 * Wraps a call E2EE key for multiple recipients.
 *
 * @param callKey - The call E2EE key to distribute
 * @param recipients - Array of recipient public keys with identity IDs
 * @param callId - Call id bound into wrap AAD
 * @returns Array of wrapped call keys, one per recipient
 */
export function wrapCallKeyForRecipients(callKey, recipients, callId) {
    return recipients.map(({ identityId, publicKeys }) => wrapCallKeyForRecipient(callKey, publicKeys, identityId, callId));
}
function callWrapAad(wrapped, callId) {
    if (wrapped.wrapVersion !== WRAP_VERSION_AAD) {
        return undefined;
    }
    if (!callId || !wrapped.routingTag) {
        throw new Error('Call wrap AAD fields are required for v2 unwrap');
    }
    return buildCallWrapAad(wrapped.recipientIdentityId, wrapped.routingTag, callId);
}
/**
 * Unwraps a call E2EE key received from the call initiator.
 *
 * @param wrapped - Wrapped call key
 * @param ecdhPrivate - Recipient's X25519 private key
 * @param kemPrivate - Recipient's ML-KEM private key
 * @param profile - Crypto profile
 * @param callId - Call id used at wrap time (required for v2 wraps)
 * @returns Unwrapped call E2EE key (32 bytes)
 */
export function unwrapCallKey(wrapped, ecdhPrivate, kemPrivate, profile = 'default', callId) {
    const sharedSecret = hybridDecapsulate(ecdhPrivate, kemPrivate, wrapped.ephemeralPublicKey, wrapped.kemCiphertext, profile);
    const aad = callWrapAad(wrapped, callId);
    return decrypt(sharedSecret, wrapped.wrappedKey, wrapped.wrappingNonce, profile, aad);
}
const SUPPORTED_PROFILES = ['default', 'cnsa2'];
/**
 * Finds and unwraps the call key addressed to a specific recipient.
 *
 * Tries the requested profile first, then falls back to other supported
 * profiles in case the key was wrapped with a different profile than expected.
 *
 * @param wrappedKeys - Array of wrapped call keys
 * @param recipientIdentityId - Identity ID of the recipient
 * @param ecdhPrivate - Recipient's X25519 private key
 * @param kemPrivate - Recipient's ML-KEM private key
 * @param profile - Crypto profile to try first
 * @param callId - Call id used at wrap time (required for v2 wraps)
 * @returns Unwrapped call key, or null if no key was addressed to this recipient
 */
export function findAndUnwrapCallKey(wrappedKeys, recipientIdentityId, ecdhPrivate, kemPrivate, profile = 'default', callId) {
    const candidates = wrappedKeys.filter((wk) => wk.recipientIdentityId === recipientIdentityId);
    if (candidates.length === 0) {
        return null;
    }
    for (const wrapped of candidates) {
        try {
            return unwrapCallKey(wrapped, ecdhPrivate, kemPrivate, profile, callId);
        }
        catch { /* try next candidate */ }
    }
    for (const fallbackProfile of SUPPORTED_PROFILES) {
        if (fallbackProfile === profile)
            continue;
        for (const wrapped of candidates) {
            try {
                return unwrapCallKey(wrapped, ecdhPrivate, kemPrivate, fallbackProfile, callId);
            }
            catch { /* try next */ }
        }
    }
    throw new Error('Failed to unwrap call key');
}
//# sourceMappingURL=index.js.map